import { useCallback, useId, useRef, useState, type ReactNode } from "react";
import clsx from "clsx";
import { Button, type ButtonSize, type ButtonVariant } from "../Button/Button.js";
import { ActionMenu, type ActionMenuItem, type MenuPlacement } from "./ActionMenu.js";
import { useMenuDismiss } from "./useMenuDismiss.js";
import styles from "./ActionMenu.module.scss";

export interface MenuButtonProps {
  /**
   * The actions. **Empty renders NOTHING** — a "More" button that opens an empty menu is a control that
   * promises something and delivers a blank panel, so the caller passes the conditional array and never
   * branches (the `SplitButton` rule, applied to a trigger that has no primary action to fall back to).
   */
  items: ActionMenuItem[];
  /** The trigger's visible content — usually an icon and a word. */
  children: ReactNode;
  /**
   * Accessible name for the trigger. Say what the menu HOLDS, not "more" — but keep the visible word
   * inside it ("More show actions" contains "More"), or the name and the label disagree (WCAG 2.5.3).
   */
  menuLabel: string;
  placement?: MenuPlacement;
  variant?: ButtonVariant;
  size?: ButtonSize;
  disabled?: boolean;
  className?: string;
}

/**
 * `MenuButton` — one button that opens a menu of actions. The overflow half of a crowded action bar.
 *
 * It is NOT a `SplitButton` with the primary half removed: a split button's contract is *one main action,
 * plus related ones*, and pressing its left half does something. This one has no main action at all — the
 * press only reveals the list — which is a different promise to the person using it. What the two genuinely
 * share is the popup and its dismiss behaviour, and that is exactly what they both import from
 * `ActionMenu`.
 *
 * The `aria-haspopup="menu"` controls in the app's NAV chrome are still their own thing — different panel,
 * different contents (an avatar, a role sub-line, a current-selection marker), and a trigger that is an
 * identity rather than a verb. They share the popup's DISMISS contract and nothing else, which is why they
 * live in `AccountMenu` and import `useMenuDismiss` from here. Don't fold either into the other.
 */
export function MenuButton({
  items,
  children,
  menuLabel,
  placement = "below",
  variant = "ghost",
  size,
  disabled,
  className,
}: MenuButtonProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();
  const close = useCallback(() => setOpen(false), []);
  useMenuDismiss(open, close, rootRef, triggerRef);

  if (items.length === 0) return null;

  return (
    <div className={clsx(styles.root, className)} ref={rootRef}>
      <Button
        ref={triggerRef}
        variant={variant}
        size={size}
        disabled={disabled}
        aria-label={menuLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        {children}
      </Button>
      {open && <ActionMenu id={menuId} items={items} placement={placement} onSelected={close} />}
    </div>
  );
}
